import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CloseRounded from '@mui/icons-material/CloseRounded';
import HelpRounded from '@mui/icons-material/HelpRounded';
import { Box, Grid, IconButton, Stack, Typography } from '@mui/material';
import { useSetAtom } from 'jotai';
import PropTypes from 'prop-types';

import { useConfirm } from 'src/components/app/context/ConfirmContext';

import BasicData from './components/BasicData';
import BasicDataHelpModal from './components/BasicDataHelpModal';
import KPISelector from './components/KPISelector';
import Pinboard from './components/Pinboard';
import { actionsAtom } from './atoms';
import { useTouchActionPlan } from './hooks';

import { styles } from './index.styles';

const Action = ({
  index,
  actionPlanId,
  actionId,
  editable = false,
  errors,
}) => {
  const { t } = useTranslation('planAction');
  const setActions = useSetAtom(actionsAtom);
  const [showModal, setShowModal] = useState(false);
  const { touchActionPlan } = useTouchActionPlan();
  const confirm = useConfirm();

  const handleDelete = async () => {
    await confirm({
      description: 'planAction:Statistics.Coverage.SingleActionDelete',
      title: 'planAction:ActionPlan.DeleteSingleAction',
    })
      .then(() => {
        setActions((prev) => prev.filter((id) => id !== actionId));
        touchActionPlan();
      })
      .catch(() => undefined);
  };

  return (
    <Stack
      direction="column"
      sx={{
        mb: 5,
      }}
    >
      <Stack direction="row" sx={{ p: 2, ...styles.header }}>
        <Typography variant="h5">{`${index + 1}. ${t('ActionPlan.Action')}`}</Typography>
        <Box>
          <IconButton size="small" onClick={() => setShowModal(true)}>
            <HelpRounded color="primary" />
          </IconButton>
          <BasicDataHelpModal
            isOpen={showModal}
            onClose={() => setShowModal(false)}
          />
          {editable && (
            <IconButton size="small" onClick={handleDelete}>
              <CloseRounded color="primary" />
            </IconButton>
          )}
        </Box>
      </Stack>
      <Box sx={{ px: 1, ...styles.content }}>
        <Grid
          container
          columnSpacing={2}
          sx={{
            py: 2,
          }}
        >
          <Grid item xs={12} md={6} xl={4}>
            <Box
              sx={{
                height: '100%',
              }}
            >
              <KPISelector
                actionId={actionId}
                editable={editable}
                errors={errors}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={6} xl={4}>
            <Box
              sx={{
                height: '100%',
              }}
            >
              <BasicData
                actionId={actionId}
                editable={editable}
                errors={errors}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={6} xl={4}>
            <Box
              sx={{
                height: '100%',
              }}
            >
              <Pinboard
                actionPlanId={actionPlanId}
                actionId={actionId}
                editable={editable}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Stack>
  );
};

Action.propTypes = {
  index: PropTypes.number.isRequired,
  actionPlanId: PropTypes.number,
  actionId: PropTypes.number.isRequired,
  editable: PropTypes.bool,
  errors: PropTypes.shape({
    title: PropTypes.string,
    notes: PropTypes.string,
  }),
};

export default Action;
